<template>
	<div
		class="relative bg-white rounded-2xl overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-300 hover:shadow-2xl"
		:class="{ 'ring-2 ring-accent shadow-xl': selected }"
		@click="onSelect"
		role="button"
		:aria-pressed="selected"
		tabindex="0"
		@keydown.enter.prevent="onSelect"
		@keydown.space.prevent="onSelect"
	>
		<!-- Top accent bar -->
		<div
			class="h-1.5 w-full transition-all duration-300"
			:class="selected ? 'bg-accent' : 'bg-primary group-hover:bg-accent'"
		></div>

		<div class="p-6">
			<!-- Header with avatar and selection -->
			<div class="flex items-start gap-4 mb-5">
				<!-- Avatar -->
				<div class="relative flex-shrink-0">
					<div
						v-if="hasAvatar"
						class="w-16 h-16 rounded-2xl overflow-hidden bg-surface shadow-md ring-2 ring-border group-hover:ring-accent/30 transition-all duration-300"
					>
						<img
							:src="valuer.avatar"
							alt="Avatar"
							class="w-full h-full object-cover"
							loading="lazy"
							@error="onAvatarError"
						/>
					</div>
					<div
						v-else
						class="w-16 h-16 rounded-2xl flex items-center justify-center bg-primary text-white font-bold text-xl shadow-md group-hover:shadow-lg transition-all duration-300"
					>
						{{ initials }}
					</div>
					<!-- Status dot -->
					<div
						class="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm"
					></div>
				</div>

				<!-- Name and ID -->
				<div class="flex-1 min-w-0">
					<h3
						class="text-xl font-bold text-text-primary mb-1 truncate group-hover:text-primary transition-colors duration-200"
					>
						{{ valuer.name }}
					</h3>
					<div
						class="inline-flex items-center gap-1.5 bg-surface px-2.5 py-1 rounded-lg border border-border"
					>
						<svg
							class="w-3.5 h-3.5 text-text-secondary"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
							></path>
						</svg>
						<span class="text-xs font-semibold text-primary">{{
							valuer.id
						}}</span>
					</div>
				</div>

				<!-- Selection badge -->
				<div v-if="selected" class="flex-shrink-0">
					<div
						class="w-10 h-10 rounded-xl bg-accent flex items-center justify-center text-white shadow-lg"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="3"
								d="M5 13l4 4L19 7"
							></path>
						</svg>
					</div>
				</div>
			</div>

			<!-- Areas of expertise -->
			<div class="mb-5">
				<div class="flex items-center gap-2 mb-2">
					<svg
						class="w-4 h-4 text-accent"
						fill="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
						></path>
					</svg>
					<span
						class="text-xs font-bold text-text-secondary uppercase tracking-wide"
						>Cover Areas</span
					>
				</div>
				<div class="flex flex-wrap gap-2">
					<span
						v-for="(area, idx) in valuer.areas"
						:key="area + idx"
						class="text-sm font-medium text-text-primary bg-surface hover:bg-primary hover:text-white border border-border hover:border-primary px-3 py-1.5 rounded-lg transition-all duration-200 cursor-default"
					>
						{{ area }}
					</span>
				</div>
			</div>

			<!-- Contact information cards -->
			<div class="grid grid-cols-1 gap-3 mb-5">
				<!-- Phone -->
				<div
					class="bg-surface border border-border rounded-xl p-3 hover:border-primary/30 hover:shadow-sm transition-all duration-200"
				>
					<div class="flex items-center gap-3">
						<div
							class="w-9 h-9 rounded-lg bg-white border border-border flex items-center justify-center flex-shrink-0"
						>
							<svg
								class="w-4 h-4 text-primary"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
								></path>
							</svg>
						</div>
						<div class="min-w-0 flex-1">
							<div
								class="text-xs text-text-secondary font-medium mb-0.5"
							>
								Phone Number
							</div>
							<div
								class="text-sm text-text-primary font-semibold truncate"
							>
								{{ valuer.phone || 'Not provided' }}
							</div>
						</div>
					</div>
				</div>

				<!-- Email -->
				<div
					class="bg-surface border border-border rounded-xl p-3 hover:border-primary/30 hover:shadow-sm transition-all duration-200"
				>
					<div class="flex items-center gap-3">
						<div
							class="w-9 h-9 rounded-lg bg-white border border-border flex items-center justify-center flex-shrink-0"
						>
							<svg
								class="w-4 h-4 text-primary"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
								></path>
							</svg>
						</div>
						<div class="min-w-0 flex-1">
							<div
								class="text-xs text-text-secondary font-medium mb-0.5"
							>
								Email Address
							</div>
							<div
								class="text-sm text-text-primary font-semibold truncate"
							>
								{{ valuer.email || 'Not provided' }}
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Action buttons -->
			<div class="flex items-center gap-3">
				<button
					class="flex-1 px-4 py-3 bg-primary hover:bg-primary-light text-white text-sm font-bold rounded-xl shadow-md hover:shadow-lg active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2"
					@click.stop="onViewProgress"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
						></path>
					</svg>
					View Progress
				</button>

				<button
					v-if="showAssign"
					class="px-4 py-3 bg-accent hover:bg-accent-light text-white text-sm font-bold rounded-xl shadow-md hover:shadow-lg active:scale-95 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all duration-200"
					@click.stop="$emit('assign', valuer)"
				>
					Assign
				</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ValuationCard',
	props: {
		valuer: {
			type: Object,
			required: true,
		},
		selected: {
			type: Boolean,
			default: false,
		},
		showAssign: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			avatarErrored: {},
		};
	},
	computed: {
		initials() {
			if (!this.valuer || !this.valuer.name) return '';
			const parts = this.valuer.name.split(' ').filter(Boolean);
			var first = '';
			var second = '';
			if (parts.length > 0 && parts[0] && parts[0].length > 0)
				first = parts[0][0];
			if (parts.length > 1 && parts[1] && parts[1].length > 0)
				second = parts[1][0];
			return (first + second).toUpperCase();
		},
		hasAvatar() {
			return !!(
				this.valuer &&
				this.valuer.avatar &&
				!this.avatarErrored[this.valuer.id]
			);
		},
	},
	methods: {
		onSelect() {
			this.$emit('select', this.valuer);
		},
		onViewProgress() {
			this.$emit('view-progress', this.valuer);
		},
		onAvatarError(e) {
			if (this.valuer && this.valuer.id) {
				this.$set(this.avatarErrored, this.valuer.id, true);
			}
			try {
				e.target.style.display = 'none';
			} catch (err) {}
		},
	},
};
</script>

<style scoped>
/* Minimal custom styles - relies on Tailwind */
</style>
